import { Button, colors, TextField, Tooltip } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/AddOutlined";
import clsx from "clsx";

import React from "react";
import { useSelector } from "react-redux";

import { ConfigureDestinationsModal } from "../../modals/ConfigureDestinationsModal";

import { DestinationConfigRow } from "../shared/ConfigPageRow";
import { useModalStyles } from "../../modals/styles";
import useFirstDestinationOfType from "../../../hooks/useFirstDestinationOfType";

const CredentialsModal = ({
  destination,
  open,
  onClose,
  handleUpdateConfigureDest,
  existingConfig = {},
}) => {
  const mixpanel = useFirstDestinationOfType("mixpanel") || { settings: {} };
  const { settings: allSettings, id } = mixpanel;

  const limitSettings = ["token", "secret"];

  if (!id) {
    limitSettings.push("preferAnonymousIdForDeviceId");
  }

  return (
    <ConfigureDestinationsModal
      limitSettings={limitSettings}
      open={open}
      includeHeader={true}
      settings={allSettings}
      dest={destination}
      onClose={onClose}
      onSave={handleUpdateConfigureDest}
      existingConfig={existingConfig}
    />
  );
};

const CredentialsRow = ({ setConfigModalOpen }) => {
  const config = (useFirstDestinationOfType("mixpanel") || { settings: {} })
    .settings;
  const idSet = !!config.token;

  return (
    <DestinationConfigRow
      name="Credentials"
      textColor={idSet ? "" : colors.red[600]}
      labelText={idSet ? "Configured" : "Not configured"}
      onOpenConfigModal={() => setConfigModalOpen("credentials")}
    />
  );
};

const GroupIdentifierTraitListConfigurationControl = ({
  field,
  value = [],
  onChange,
}) => {
  const classes = useModalStyles();

  const onGroupKeyChange = (index, newGroupKey) => {
    const newValue = value.slice();
    newValue[index] = newGroupKey;
    onChange(field.key, newValue);
  };

  const onDelete = (index) => {
    const newValue = value.slice();
    newValue.splice(index, 1);
    onChange(field.key, newValue);
  };

  return (
    <div className="justify-items-stretch" style={{ width: "100%" }}>
      {value.map((groupKey, index) => {
        return (
          <div className="flex items-stretch" key={index}>
            <GroupIdentifierTraitConfigurationControl
              field={field}
              groupKey={groupKey}
              onGroupKeyChange={(newGroupKey) =>
                onGroupKeyChange(index, newGroupKey)
              }
            />
            <span className="flex flex-wrap justify-end">
              <Tooltip title="Delete Group Identifier Trait">
                <div
                  className="hover:bg-navy-200 p-1 rounded-full flex justify-center items-center"
                  onClick={() => onDelete(index)}
                >
                  <CloseIcon className={classes.closeIcon} />
                </div>
              </Tooltip>
            </span>
          </div>
        );
      })}
      <Button
        color="primary"
        className="text-xs bg-navy-100 text-navy-800 inline-block inline-flex items-center px-3 py-1 rounded cursor-pointer hover:bg-navy-200"
        onClick={() => onChange(field.key, [...value, { key: "", value: "" }])}
      >
        <AddIcon className="mr-1" style={{ width: 16 }} />
        Add Group Identifier Trait
      </Button>
    </div>
  );
};

const GroupIdentifierTraitConfigurationControl = ({
  field,
  groupKey,
  onGroupKeyChange,
}) => {
  const classes = useModalStyles();

  const handleGroupKeyChange = (groupKeyChanges) => {
    const newGroupKey = { ...groupKey, ...groupKeyChanges };
    onGroupKeyChange(newGroupKey);
  };

  return (
    <div className="justify-items-stretch" style={{ width: "100%" }}>
      <TextField
        fullWidth
        autoComplete="off"
        label="Group Identifier Trait"
        name="Group Identifier Trait"
        onChange={(event) =>
          handleGroupKeyChange({ value: event.target.value })
        }
        value={groupKey.value || ""}
        key={field.name}
        variant="outlined"
        type="text"
        inputProps={{ style: { fontFamily: "monospace" } }}
        className={clsx(classes.inputField, "mb-2 fs-exclude")}
      />
    </div>
  );
};

const GroupIdentifierTraitsWrapperControl = ({
  field,
  value = [],
  onChange,
}) => {
  const innerValue = value.map((v) => ({ key: "", value: v }));
  const innerOnChange = (fieldKey, innerValue) => {
    const value = innerValue.map((obj) => obj.value);
    onChange(fieldKey, value);
  };

  return (
    <GroupIdentifierTraitListConfigurationControl
      field={field}
      value={innerValue}
      onChange={innerOnChange}
    />
  );
};

const ConfigurationModal = ({
  destination,
  open,
  onClose,
  handleUpdateConfigureDest,
  existingConfig = {},
}) => {
  const mixpanel = useFirstDestinationOfType("mixpanel") || { settings: {} };
  const { settings: allSettings, id } = mixpanel;

  const limitSettings = [
    "groupIdentifierTraits",
    "ip",
    "crossSubdomainCookie",
    "persistence",
    "consolidatedPageCalls",
    "enableEuropeanUnionEndpoint",
    "secureCookie",
    "ignore_dnt",
    "mergeAnonymous",
    "defaultIdentifyTransformations",
  ];

  if (id) {
    limitSettings.push("preferAnonymousIdForDeviceId");
  }

  return (
    <ConfigureDestinationsModal
      settingsToControl={{
        groupIdentifierTraits: GroupIdentifierTraitsWrapperControl,
      }}
      limitSettings={limitSettings}
      open={open}
      settings={allSettings}
      dest={destination}
      onClose={onClose}
      onSave={handleUpdateConfigureDest}
      existingConfig={existingConfig}
    />
  );
};

const ConfigurationRow = ({ setConfigModalOpen }) => {
  const config = (useFirstDestinationOfType("mixpanel") || { settings: {} })
    .settings;
  const enabled = !!config.token;

  return (
    <DestinationConfigRow
      disabled={!enabled}
      name="Options"
      labelText="Optional"
      onOpenConfigModal={() => {
        if (enabled) {
          setConfigModalOpen();
        }
      }}
    />
  );
};

const CREDENTIALS = "CREDENTIALS";
const OPTIONS = "OPTIONS";

const Rows = {
  CREDENTIALS: CredentialsRow,
  OPTIONS: ConfigurationRow,
};

const Modals = {
  CREDENTIALS: CredentialsModal,
  OPTIONS: ConfigurationModal,
};

const RowOrder = [CREDENTIALS, OPTIONS];

export default {
  Rows,
  RowOrder,
  Modals,
};
